import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Public as GlobeIcon,
  Check as CheckIcon,
  Close as XIcon,
  Visibility as EyeIcon,
  Description as FileTextIcon,
  AccessTime as ClockIcon,
  Warning as AlertTriangleIcon,
} from "@mui/icons-material";

interface ImmigrationDocument {
  id: number;
  employeeName: string;
  employeeId: string;
  documentType: string;
  submissionDate: string;
  expiryDate: string;
  status: "Pending" | "Review Required" | "Approved" | "Rejected";
  urgency: "Low" | "Medium" | "High";
  progress: number;
}

const ImmigrationDocsTab: React.FC = () => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState("");

  const [documents, setDocuments] = useState<ImmigrationDocument[]>([
    {
      id: 1,
      employeeName: "Raj Patel",
      employeeId: "EMP001",
      documentType: "H-1B Visa Extension",
      submissionDate: "2024-12-20",
      expiryDate: "2025-03-15",
      status: "Review Required",
      urgency: "High",
      progress: 75,
    },
    {
      id: 2,
      employeeName: "Li Wei",
      employeeId: "EMP002",
      documentType: "Green Card Application",
      submissionDate: "2024-12-18",
      expiryDate: "2025-06-30",
      status: "Pending",
      urgency: "Medium",
      progress: 45,
    },
    {
      id: 3,
      employeeName: "Maria Garcia",
      employeeId: "EMP003",
      documentType: "Work Permit Renewal",
      submissionDate: "2024-12-15",
      expiryDate: "2025-02-28",
      status: "Review Required",
      urgency: "High",
      progress: 85,
    },
    {
      id: 4,
      employeeName: "Ahmed Hassan",
      employeeId: "EMP004",
      documentType: "TN Visa Application",
      submissionDate: "2024-12-10",
      expiryDate: "2025-12-31",
      status: "Pending",
      urgency: "Low",
      progress: 30,
    },
    {
      id: 5,
      employeeName: "Elena Kowalski",
      employeeId: "EMP005",
      documentType: "I-9 Verification",
      submissionDate: "2024-12-08",
      expiryDate: "2025-01-15",
      status: "Review Required",
      urgency: "Medium",
      progress: 90,
    },
  ]);

  const pendingCount = documents.filter(doc =>
    doc.status === "Pending" || doc.status === "Review Required"
  ).length;

  const handleApprove = (id: number) => {
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === id ? { ...doc, status: "Approved" as const, progress: 100 } : doc
      )
    );
    setSuccessMessage("Document approved successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReject = (id: number) => {
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === id ? { ...doc, status: "Rejected" as const } : doc
      )
    );
    setSuccessMessage("Document rejected.");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleView = (id: number) => {
    console.log(`Viewing document ${id}`);
    setSuccessMessage("Opening document viewer...");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Review Required":
        return "info";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "success";
      default:
        return "default";
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case "High":
        return <AlertTriangleIcon />;
      case "Medium":
        return <ClockIcon />;
      case "Low":
        return <CheckIcon />;
      default:
        return <FileTextIcon />;
    }
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <GlobeIcon color="primary" sx={{ mr: 1 }} />
          Immigration Documents
          {pendingCount > 0 && (
            <Chip
              label={pendingCount}
              color="error"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pendingCount} documents requiring review and approval
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Documents Grid */}
      <Grid container spacing={3}>
        {documents.map((document) => (
          <Grid item xs={12} md={6} key={document.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                border: document.urgency === "High" ? `2px solid ${theme.palette.error.main}` :
                        document.status === "Review Required" ? `2px solid ${theme.palette.info.main}` : "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <GlobeIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {document.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {document.employeeId}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexDirection: "column", alignItems: "end" }}>
                    <Chip
                      label={document.status}
                      color={getStatusColor(document.status) as any}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      icon={getUrgencyIcon(document.urgency)}
                      label={document.urgency}
                      color={getUrgencyColor(document.urgency) as any}
                      variant="filled"
                      size="small"
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {document.documentType}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Submitted:</strong> {document.submissionDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Expires:</strong> {document.expiryDate}
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Processing Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {document.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={document.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EyeIcon />}
                    onClick={() => handleView(document.id)}
                  >
                    View
                  </Button>
                  {(document.status === "Pending" || document.status === "Review Required") && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => handleApprove(document.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<XIcon />}
                        onClick={() => handleReject(document.id)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {documents.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <GlobeIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No immigration documents found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Immigration documents will appear here when submitted by employees.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ImmigrationDocsTab;